import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card/card';
import { FlowBoard, READ_ONLY_FLOW_BOARD_CONFIG } from '@/components/FlowNode';
import { VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { ApprovalReturnHistory } from '@/features/boe-task/components/ApprovalReturn/ApprovalReturnHistory';
import { getApprovalReturnHistory } from '@/features/boe-task/components/ApprovalReturn/approvalReturnInfoSelectors';
import { ApprovalSendBackDialog } from '@/features/boe-task/components/ApprovalTaskDetail/ApprovalSendBackDialog';
import { approvalSendBackDialogKeyPrefix } from '@/features/boe-task/lib/i18nSelectors';
import { handleApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { ApprovalSubmitTaskContent } from '../-components/approval/ApprovalSubmitTaskContent';
import { canApproveApprovalTask, convertApprovalFlowToFlowData, useApproveApplication, useGetApprovalTaskDetail, useSendBackApplication } from './-query';
export const Route = createFileRoute('/_individual/individual/task/$id/approval/')({
  component: RouteComponent
});
function RouteComponent() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.tasks.approvalDialog'
  });
  const {
    t: tAction
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.action'
  });
  const {
    t: tSendBack
  } = useTranslation(defaultNS, {
    keyPrefix: approvalSendBackDialogKeyPrefix
  });
  const {
    i18n
  } = useTranslation();
  const {
    id
  } = Route.useParams();
  const {
    data
  } = useGetApprovalTaskDetail(id);
  const navigate = Route.useNavigate();

  // 承認フローデータをFlowNodeData形式に変換
  const flowData = convertApprovalFlowToFlowData(data.approvalFlow);

  // 承認 / 差し戻しの対象となる提出版。BE は「承認者が見ていた版」と最新版の一致を
  // 要求するため（楽観ロック）、取れないとき（未提出）は操作させない。
  // 「最新」の判定は BE 側（イベントストアの version 順）に委ねる。submissions の並びや
  // submittedAt からは再計算できない（同一リクエスト内の操作は時刻が同値になる）
  const expectedSubmissionEventId = data.approvalReturnInfo?.latestSubmissionEventId ?? undefined;

  // DC-8723: 承認待ちでない承認タスク（既に承認済み・却下・差戻し済み）では
  // 承認ボタンを表示しない。判定ロジックは -query/utils.ts に切り出し単体テスト済み。
  const canApprove = canApproveApprovalTask(data.approvalFlow, data.operationTicketId) && expectedSubmissionEventId !== undefined;
  const handleNavigateBack = useCallback(() => {
    navigate({
      to: `/individual`
    });
  }, [navigate]);
  const {
    mutate: approveApplication,
    isPending
  } = useApproveApplication({
    onSuccess: () => {
      toast({
        description: t('approvalSuccess'),
        variant: 'default'
      });
      navigate({
        to: `/individual`
      });
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  });
  const handleSubmit = useCallback(() => {
    // canApprove が false のときはボタン自体を出さないので、ここには来ない
    if (!expectedSubmissionEventId) return;
    approveApplication({
      taskId: id,
      expectedSubmissionEventId
    });
  }, [approveApplication, id, expectedSubmissionEventId]);
  const {
    mutateAsync: sendBackApplication,
    isPending: isSendingBack
  } = useSendBackApplication({
    onSuccess: () => {
      toast({
        description: tSendBack($ => $.success),
        variant: 'default'
      });
      navigate({
        to: `/individual`
      });
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  });

  // 差し戻せる状態のときだけボタンを出す。可否判定は BE の canSendBack が正
  // （設定 + 承認待ち + 差し戻し経路の AND。表示と実行が同じ判定を見る）
  const canSendBack = data.approvalReturnInfo?.canSendBack === true && expectedSubmissionEventId !== undefined;
  const handleSendBack = useCallback(async (reason?: string) => {
    if (!expectedSubmissionEventId) return;
    await sendBackApplication({
      taskId: id,
      reason,
      expectedSubmissionEventId
    });
  }, [sendBackApplication, id, expectedSubmissionEventId]);
  return <div className="p-6">
      <VStack className="gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{data.approvalTaskName}</CardTitle>
          </CardHeader>
          <CardContent>
            <VStack className="gap-4">
              {/* 過去に差し戻していれば履歴を出す（承認者が経緯を追えるようにする） */}
              <ApprovalReturnHistory history={getApprovalReturnHistory(data.approvalReturnInfo)} />

              <div>
                <Text variant="labelLarge" className="mb-2">
                  {t('submitTaskContent')}
                </Text>
                <ApprovalSubmitTaskContent submitTaskForm={data.submitTaskForm} />
              </div>

              <div>
                <Text variant="labelLarge" className="mb-2">
                  {t('approvalFlow')}
                </Text>
                <div className="h-96 border rounded">
                  <FlowBoard data={flowData} config={READ_ONLY_FLOW_BOARD_CONFIG} className="w-full h-full" />
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={handleNavigateBack}>
                  {tAction('cancel')}
                </Button>
                <div className="flex gap-2">
                  {canSendBack && <ApprovalSendBackDialog onSubmit={handleSendBack} isLoading={isSendingBack} trigger={<Button variant="outline" disabled={isSendingBack}>
                          {tAction('remand')}
                        </Button>} />}
                  {canApprove && <Button onClick={handleSubmit} disabled={isPending || isSendingBack}>
                      {isPending ? t('approving') : t('approve')}
                    </Button>}
                </div>
              </div>
            </VStack>
          </CardContent>
        </Card>
      </VStack>
    </div>;
}